import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toDubai } from '../../../utils/dubaiTime';
import './statistics.css';
import './onboardingStat.css';

function safeNum(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
}

// Week label anchored in Asia/Dubai (item 8): the old version did Monday/Sunday
// arithmetic on device-local Date fields, so a machine west of UTC labelled the
// whole chart with the previous week. Same "dd MMM – dd MMM" visual.
function formatWeekRangeLabel(weekStart) {
  if (!weekStart) return '-';
  const base = toDubai(weekStart);
  if (!base) return String(weekStart);

  const mon = base.startOf('day').minus({ days: base.weekday - 1 }); // luxon: 1 = Monday
  const sun = mon.plus({ days: 6 });
  return `${mon.toFormat('dd MMM')} – ${sun.toFormat('dd MMM')}`;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function MiniBarChart({ title, subtitle, data, valueKey, accent }) {
  const wrapRef = useRef(null);
  const tipRef = useRef(null);

  const [tooltip, setTooltip] = useState({
    open: false,
    x: 0,
    y: 0,
    label: '',
    value: 0,
  });

  const [tipSize, setTipSize] = useState({ w: 160, h: 80 });

  const maxValue = useMemo(() => Math.max(1, ...data.map((d) => safeNum(d[valueKey]))), [data, valueKey]);

  const total = useMemo(() => data.reduce((sum, d) => sum + safeNum(d[valueKey]), 0), [data, valueKey]);

  const last = data.length ? safeNum(data[data.length - 1][valueKey]) : 0;
  const prev = data.length > 1 ? safeNum(data[data.length - 2][valueKey]) : 0;
  const delta = last - prev;

  const ticks = useMemo(() => {
    const steps = 4;
    const arr = [];
    for (let i = 0; i <= steps; i += 1) arr.push(Math.round((maxValue * i) / steps));
    return arr;
  }, [maxValue]);

  // Measure tooltip size whenever it opens/updates, so we can clamp properly
  useEffect(() => {
    if (!tooltip.open) return;
    if (!tipRef.current) return;
    const rect = tipRef.current.getBoundingClientRect();
    setTipSize({ w: Math.ceil(rect.width), h: Math.ceil(rect.height) });
  }, [tooltip.open, tooltip.label, tooltip.value]);

  const openTip = (e, d) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();

    // anchor to mouse position relative to chart wrapper
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setTooltip({
      open: true,
      x,
      y,
      label: formatWeekRangeLabel(d.week_start),
      value: safeNum(d[valueKey]),
    });
  };

  const moveTip = (e) => {
    // IMPORTANT: Only move tooltip while it's open (and therefore mouse is over a bar)
    if (!tooltip.open) return;
    if (!wrapRef.current) return;

    const rect = wrapRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setTooltip((prevState) => ({ ...prevState, x, y }));
  };

  const closeTip = () => {
    setTooltip((prevState) => ({ ...prevState, open: false }));
  };

  // Clamp tooltip inside chart bounds
  const clampedStyle = useMemo(() => {
    if (!wrapRef.current) {
      return { left: `${tooltip.x + 12}px`, top: `${tooltip.y - 10}px` };
    }

    const pad = 10;
    const anchorOffsetX = 12;
    const anchorOffsetY = -10;

    const containerW = wrapRef.current.clientWidth;
    const containerH = wrapRef.current.clientHeight;

    // preferred position
    let left = tooltip.x + anchorOffsetX;
    let top = tooltip.y + anchorOffsetY;

    // clamp within bounds
    left = clamp(left, pad, Math.max(pad, containerW - tipSize.w - pad));
    top = clamp(top, pad, Math.max(pad, containerH - tipSize.h - pad));

    return { left: `${left}px`, top: `${top}px` };
  }, [tooltip.x, tooltip.y, tipSize.w, tipSize.h]);

  return (
    <div className="statistics-onboarding-card" style={{ '--accent': accent }}>
      <div className="statistics-onboarding-card-header">
        <div>
          <div className="statistics-onboarding-card-title">{title}</div>
          <div className="statistics-onboarding-card-subtitle">{subtitle}</div>
        </div>

        <div className="statistics-onboarding-kpis">
          <div className="statistics-onboarding-kpi">
            <div className="statistics-onboarding-kpi-label">Total</div>
            <div className="statistics-onboarding-kpi-value">{total}</div>
          </div>

          <div className="statistics-onboarding-kpi">
            <div className="statistics-onboarding-kpi-label">This week</div>
            <div className="statistics-onboarding-kpi-value">{last}</div>
            <div className={`statistics-onboarding-kpi-delta ${delta >= 0 ? 'is-up' : 'is-down'}`}>
              {delta >= 0 ? `+${delta}` : `${delta}`} vs last week
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip should only exist while hovering a bar */}
      <div className="statistics-onboarding-chart" ref={wrapRef}>
        <div className="statistics-onboarding-plot">
          <div className="statistics-onboarding-gridlines">
            {ticks.map((t, idx) => (
              <div
                key={`${valueKey}-tick-${t}-${idx}`}
                className="statistics-onboarding-gridline"
                style={{ bottom: `${(idx / (ticks.length - 1)) * 100}%` }}
              />
            ))}
          </div>

          <div className="statistics-onboarding-yaxis">
            {ticks
              .slice()
              .reverse()
              .map((t, idx) => (
                <div key={`${valueKey}-ylabel-${t}-${idx}`} className="statistics-onboarding-ylabel">
                  {t}
                </div>
              ))}
          </div>

          <div className="statistics-onboarding-bars">
            {data.map((d, idx) => {
              const value = safeNum(d[valueKey]);

              const CHART_HEIGHT_PX = 180;
              const usable = Math.max(60, CHART_HEIGHT_PX - 12);
              const rawPx = Math.round((value / maxValue) * usable);
              const barPx = value <= 0 ? 2 : Math.max(6, rawPx);

              return (
                <div key={`${valueKey}-${idx}`} className="statistics-onboarding-bar-col">
                  <div
                    className="statistics-onboarding-bar"
                    style={{ height: `${barPx}px` }}
                    onMouseEnter={(e) => openTip(e, d)}
                    onMouseMove={moveTip}   // ✅ moves only while on bar
                    onMouseLeave={closeTip} // ✅ disappears immediately when off bar
                  />
                </div>
              );
            })}
          </div>

          <div className="statistics-onboarding-yhint">Max {maxValue}</div>

          {tooltip.open && (
            <div ref={tipRef} className="statistics-onboarding-tooltip" style={clampedStyle}>
              <div className="statistics-onboarding-tooltip-title">{tooltip.label}</div>
              <div className="statistics-onboarding-tooltip-row">
                <span className="statistics-onboarding-tooltip-key">Qty</span>
                <span className="statistics-onboarding-tooltip-val">{tooltip.value}</span>
              </div>
            </div>
          )}
        </div>

        <div className="statistics-onboarding-xaxis">
          {data.map((d, idx) => {
            const label = formatWeekRangeLabel(d.week_start);
            return (
              <div key={`${valueKey}-xl-${idx}`} className="statistics-onboarding-xcell">
                {idx % 2 === 0 ? label : ''}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Onboarded vs lost over the whole selected window — the "are we growing or
// shrinking" verdict the weekly bars can't show at a glance.
function NetSummary({ rows }) {
  const totals = useMemo(() => {
    const sum = (key) => rows.reduce((acc, r) => acc + safeNum(r[key]), 0);
    const clientsOn = sum('clients_onboarded');
    const clientsLost = sum('clients_lost');
    const propsOn = sum('properties_onboarded');
    const propsLost = sum('properties_lost');
    return {
      clientsOn,
      clientsLost,
      clientsNet: clientsOn - clientsLost,
      propsOn,
      propsLost,
      propsNet: propsOn - propsLost,
    };
  }, [rows]);

  const fmtNet = (n) => (n > 0 ? `+${n}` : `${n}`);

  return (
    <div className="statistics-onboarding-net">
      <div className="statistics-onboarding-net-title">Net over this period</div>
      <div className="statistics-onboarding-net-grid">
        <div className="statistics-onboarding-net-cell">
          <div className="statistics-onboarding-net-label">Clients</div>
          <div className="statistics-onboarding-net-detail">
            {totals.clientsOn} onboarded · {totals.clientsLost} lost
          </div>
          <div className={`statistics-onboarding-net-value ${totals.clientsNet >= 0 ? 'is-up' : 'is-down'}`}>
            {fmtNet(totals.clientsNet)}
          </div>
        </div>
        <div className="statistics-onboarding-net-cell">
          <div className="statistics-onboarding-net-label">Properties</div>
          <div className="statistics-onboarding-net-detail">
            {totals.propsOn} onboarded · {totals.propsLost} lost
          </div>
          <div className={`statistics-onboarding-net-value ${totals.propsNet >= 0 ? 'is-up' : 'is-down'}`}>
            {fmtNet(totals.propsNet)}
          </div>
        </div>
        <div className="statistics-onboarding-net-cell statistics-onboarding-net-cell--verdict">
          <div className="statistics-onboarding-net-label">Verdict</div>
          <div className={`statistics-onboarding-net-verdict ${totals.clientsNet + totals.propsNet >= 0 ? 'is-up' : 'is-down'}`}>
            {totals.clientsNet + totals.propsNet >= 0
              ? 'Growing — onboarded more than lost'
              : 'Shrinking — lost more than onboarded'}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OnboardingStat() {
  const [weeks, setWeeks] = useState(12);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/statistics/onboarding', {
        params: { weeks },
        withCredentials: true,
      });

      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      console.error('Failed to load onboarding stats', e);
      setError('Failed to load onboarding statistics.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks]);

  return (
    <div className="statistics-section">
      <div className="statistics-header">
        <div>
          <h2 className="statistics-section-title">Onboarding &amp; Churn</h2>
          <p className="statistics-subtitle">Weekly onboarded vs lost clients and properties.</p>
        </div>

        <div className="statistics-header-actions">
          <button className="statistics-refresh-button" type="button" onClick={fetchData} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="statistics-controls">
        <div className="statistics-control-card">
          <label className="statistics-label">Weeks</label>
          <select className="statistics-select" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
            <option value={6}>Last 6 weeks</option>
            <option value={8}>Last 8 weeks</option>
            <option value={12}>Last 12 weeks</option>
            <option value={16}>Last 16 weeks</option>
            <option value={26}>Last 26 weeks</option>
            <option value={52}>Last 52 weeks</option>
          </select>
        </div>

        <button className="statistics-primary-button" type="button" onClick={fetchData} disabled={loading}>
          Apply
        </button>
      </div>

      {error && <div className="statistics-error">{error}</div>}

      {!error && rows.length === 0 && !loading && <div className="statistics-empty">No onboarding data found.</div>}

      <div className="statistics-onboarding-grid">
        <MiniBarChart
          title="Clients onboarded"
          subtitle="New clients per week"
          data={rows}
          valueKey="clients_onboarded"
          accent="#ff5a60"
        />

        <MiniBarChart
          title="Properties onboarded"
          subtitle="New properties per week"
          data={rows}
          valueKey="properties_onboarded"
          accent="#2563eb"
        />

        <MiniBarChart
          title="Clients lost"
          subtitle="Clients archived per week"
          data={rows}
          valueKey="clients_lost"
          accent="#6b7280"
        />

        <MiniBarChart
          title="Properties lost"
          subtitle="Properties archived per week"
          data={rows}
          valueKey="properties_lost"
          accent="#9ca3af"
        />
      </div>

      {rows.length > 0 && <NetSummary rows={rows} />}
    </div>
  );
}
